import { useContentSingletons, useMultipleContents, usePageContent } from "@robe/client/xcore-ui";
import { Icon, Link, Stack } from "@xcorejs/ui";
import PdfIcon from "components/Icons/16/pdf.svg";
import { SubheaderNavLinkStyle } from "components/Layout/Subheader";
import ScrollLink from "components/ScrollLink";
import { useMemo } from "react";
import { News, Product, ProductDetailPage } from "xcore/types";
import { useLayout } from "xcore";
import { NewsCardNewsValuesFields } from "design-system/robe";
import { usePdfCdn } from "hooks";

export const ProductSubHeaderContent = () => {
  const { stringify, localize } = useLayout();
  const product = usePageContent<Product>();
  const [productDetail] = useContentSingletons<[ProductDetailPage]>();
  const [productRelatedNews] = useMultipleContents<[News<NewsCardNewsValuesFields>]>();

  const showKeyFeatures = useMemo(
    () => !!(localize(product.values?.keyFeatures) ?? [])?.filter((k) => k.values && stringify(k.values?.title)).length,
    [localize, product.values?.keyFeatures, stringify]
  );

  const datasheetUrl = usePdfCdn({ id: product.id, contentLocales: product.locales, type: "product" });

  return (
    <div style={{ display: "flex", alignItems: "center", height: "4.6rem", justifyContent: "center" }}>
      <Stack gap={{ _: "1rem", lg: "2rem" }}>
        <ScrollLink
          target={"#" + stringify(productDetail.values.anchorAbout)}
          hashSpy={false}
          {...SubheaderNavLinkStyle}
          padding={0}
          position="relative"
        >
          {stringify(productDetail.values.aboutMenu)}
        </ScrollLink>

        {showKeyFeatures && (
          <ScrollLink
            target={"#" + stringify(productDetail.values.anchorKeyFeatures)}
            {...SubheaderNavLinkStyle}
            className="scroll-link-merged-section"
            padding={0}
            position="relative"
          >
            {stringify(productDetail.values.keyFeaturesTitle)}
          </ScrollLink>
        )}

        <ScrollLink
          target={"#" + stringify(productDetail.values.anchorProductPictures)}
          hashSpy={false}
          {...SubheaderNavLinkStyle}
          className="scroll-link-merged-section"
          padding={0}
          position="relative"
        >
          {stringify(productDetail.values.pictures)}
        </ScrollLink>

        {product.values.videos && (
          <ScrollLink
            target={"#" + stringify(productDetail.values.anchorVideo)}
            {...SubheaderNavLinkStyle}
            padding={0}
            className="scroll-link-merged-section"
            position="relative"
          >
            {stringify(productDetail.values.productVid)}
          </ScrollLink>
        )}

        <ScrollLink
          target={"#" + stringify(productDetail.values.anchorDownloads)}
          {...SubheaderNavLinkStyle}
          padding={0}
          position="relative"
        >
          {stringify(productDetail.values.downloads)}
        </ScrollLink>

        {product.values.wheels && (
          <ScrollLink target={"#" + stringify(productDetail.values.anchorGobos)} {...SubheaderNavLinkStyle} padding={0} position="relative">
            {stringify(productDetail.values.gobosColors)}
          </ScrollLink>
        )}

        {product.values.accessories && (
          <ScrollLink
            target={"#" + stringify(productDetail.values.anchorAccessories)}
            {...SubheaderNavLinkStyle}
            padding={0}
            position="relative"
          >
            {stringify(productDetail.values.accessories)}
          </ScrollLink>
        )}

        {product.values.specification && (
          <ScrollLink
            target={"#" + stringify(productDetail.values.anchorTechnicalSpecs)}
            {...SubheaderNavLinkStyle}
            padding={0}
            position="relative"
          >
            {stringify(productDetail.values.technicalSpec)}
          </ScrollLink>
        )}

        {productRelatedNews && productRelatedNews.length > 0 && (
          <ScrollLink
            target={"#" + stringify(productDetail.values.anchorRelatedNews)}
            {...SubheaderNavLinkStyle}
            padding={0}
            position="relative"
          >
            {stringify(productDetail.values.relatedNews)}
          </ScrollLink>
        )}
        {datasheetUrl && (
          <Link
            variant="simple"
            href={datasheetUrl}
            target="_blank"
            {...SubheaderNavLinkStyle}
            padding={{ _: "0", lg: "0" }}
            title={stringify(productDetail.values.dataSheetMenu)}
          >
            <Icon svg={<PdfIcon />} mr={0} />
          </Link>
        )}
      </Stack>
    </div>
  );
};
